// Authentication utility functions

// Helper function to get cookie value
export const getCookie = (name) => {
  const cookieName = name + "=";
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return "";
};

// Helper function to set cookie
export const setCookie = (name, value, expiryHours = 24) => {
  const date = new Date();
  date.setTime(date.getTime() + (expiryHours * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/;secure;samesite=strict";
};

// Helper function to decode JWT tokens
export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return null;
  }
};

// Check if token expires within threshold (default 30 min)
export const isTokenExpiringSoon = (thresholdMinutes = 30) => {
  const idToken = getCookie('idToken');
  const accessToken = getCookie('accessToken');
  
  // If either token is missing, consider expired
  if (!idToken || !accessToken) return true;
  
  const threshold = thresholdMinutes * 60 * 1000;
  const now = Date.now();
  
  // Check idToken expiration
  const idDecoded = parseJwt(idToken);
  if (!idDecoded?.exp) return true;
  const idExpiresAt = idDecoded.exp * 1000;
  
  // Check accessToken expiration
  const accessDecoded = parseJwt(accessToken);
  if (!accessDecoded?.exp) return true;
  const accessExpiresAt = accessDecoded.exp * 1000;
  
  // Return true if either token is expiring soon
  return now > (idExpiresAt - threshold) || now > (accessExpiresAt - threshold);
};

// Refresh tokens using refresh_token
export const refreshTokens = async () => {
  const refreshToken = getCookie('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '2j7n6qlp5v84krflbvan0s9akr';
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || 'us-east-1xha300vyy.auth.us-east-1.amazoncognito.com';
  
  const response = await fetch(`https://${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken
    })
  });
  
  if (!response.ok) {
    throw new Error('Token refresh failed');
  }
  
  const tokens = await response.json();
  
  // Update cookies (refresh_token not returned on refresh - reuse existing)
  setCookie('accessToken', tokens.access_token, 24);
  setCookie('idToken', tokens.id_token, 24);
  
  console.log('Tokens refreshed successfully');
  return tokens;
};

// Get user email from JWT token
export const getUserEmail = () => {
  const idToken = getCookie('idToken');
  if (!idToken) {
    console.warn('No ID token found');
    return null;
  }
  
  const decoded = parseJwt(idToken);
  return decoded?.email || null;
};

// Get user name from JWT token
export const getUserName = () => {
  const idToken = getCookie('idToken');
  if (!idToken) return null;
  
  const decoded = parseJwt(idToken);
  return decoded?.name || decoded?.email || null;
};

// Get user groups from JWT token
export const getUserGroups = () => {
  const idToken = getCookie('idToken');
  if (!idToken) return [];
  
  const decoded = parseJwt(idToken);
  return decoded?.['cognito:groups'] || [];
};
